import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { AlertTriangle } from "lucide-react";
import type { ContractResponseDTO } from "../types/contract.types";
import { memo } from "react";

/**
 * Dialog de confirmação de exclusão de contrato.
 *
 * Exibe o número e o cliente do contrato alvo para evitar exclusão acidental.
 * `contract` pode ser `null` durante a animação de fechamento do dialog —
 * por isso é renderizado condicionalmente com `?.`.
 */
interface ContractDeleteDialogProps {
  open: boolean;
  loading: boolean;
  contract: ContractResponseDTO | null;
  onClose: () => void;
  onConfirm: () => void;
}

export const ContractDeleteDialog = memo(function ContractDeleteDialog({
  open,
  loading,
  contract,
  onClose,
  onConfirm,
}: ContractDeleteDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Excluir Contrato</DialogTitle>
          <DialogDescription>
            Tem certeza que deseja excluir este contrato?
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="p-4 bg-destructive/10 rounded-md border border-destructive/20">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">{contract?.contractNumber}</p>
                <p className="text-sm text-muted-foreground">
                  Cliente: {contract?.clientName}
                </p>
                <p className="text-sm text-destructive mt-2">
                  Esta ação não pode ser desfeita!
                </p>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={loading}>
            {loading ? "Excluindo..." : "Excluir Contrato"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
